import './index.css'
import { MdInfo as InfoIcon } from '@react-icons/all-files/md/MdInfo'
import { useTranslation } from '@disco/i18n/use-translation'
import debounce from 'lodash/debounce'
import React, { useEffect, useRef } from 'react'
import { Outlet } from 'react-router-dom'

import { WATERMARKING } from '@disco/constants/FeatureTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import {
  loadNextWatermarking,
  loadWatermarking,
  updateWatermarkingLeaksSearchParams,
} from '@disco/state-management/actions/watermarking-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import dayjs from '@disco/utils/dayjs'
import { getTrackSubheader } from '@disco/utils/tracks'

import TrackArtwork from 'components/common/tracks/track-artwork'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import InfiniteScroll from 'components/ui/infinite-scroll'
import InlineLink from 'components/ui/inline-link'
import Input from 'components/ui/input'
import MediaItem from 'components/ui/media-item'
import Spinner from 'components/ui/spinner'
import TextOverflow from 'components/ui/text-overflow'
import Tooltip from 'components/ui/tooltip'
import UserAvatar from 'components/ui/user-avatar'
import getTrackStatus from 'components/utils/track-status'

import { useDispatch, useSelector } from 'hooks/use-redux'

import WatermarkingEmptyState from '../watermarking-empty-state'

import type { WatermarkingLeakStatus } from './leak-status'
import { getWatermarkingLeakStatus } from './leak-status'

type LeakTraceItem = {
  id: number
  file_name: string
  created_at: string
  reported_by: {
    email: string
    name?: string
  }
  status: WatermarkingLeakStatus
  cancelled?: boolean
  detected_watermark_data: Array<{
    track?: any
    playlist?: {
      name: string
    }
    recipient_data?: {
      user_email?: string
      business_hostname?: string
    }
    sender_data?: {
      user_email?: string
    }
  }>
}

const LeakTracing = (): React.ReactNode => {
  const dispatch = useDispatch()
  const isFeatureEnabled = useFeatureEnabled(WATERMARKING)
  const watermarkingLeaks = useSelector(state => state.watermarking.leaks)
  const query = useSelector(state => state.watermarking.leaks.searchTerm)
  const filter = useSelector(state => state.watermarking.leaks.filters)
  const { t } = useTranslation('watermarking')
  useEffect(() => {
    dispatch(loadWatermarking(query, filter, TRACK))
  }, [])
  const debouncedQuery = useRef(
    debounce((query, filter) => {
      dispatch(loadWatermarking(query, filter, TRACK))
    }, 300),
  ).current

  const updateSearchQuery = event => {
    dispatch(
      updateWatermarkingLeaksSearchParams({
        searchTerm: event.target.value,
      }),
    )
    debouncedQuery(event.target.value, filter)
  }

  const getLastTrackData = ({ detected_watermark_data }: LeakTraceItem) => {
    return detected_watermark_data[detected_watermark_data.length - 1]
  }

  const showSpinner =
    watermarkingLeaks.searching && watermarkingLeaks.items.length === 0
  return (
    <>
      <div className="Watermarking_Header">
        <div className="Watermarking_SearchWrapper">
          <Input
            icon="search"
            placeholder="Search"
            onChange={updateSearchQuery}
            value={query}
          />
        </div>
        <div>
          <Button
            caption="Trace a leak"
            disabled={!isFeatureEnabled}
            appearance="primary"
            link="/watermarking/leak-tracing/tracing"
            tooltip={
              isFeatureEnabled ? null : (
                <>
                  To run leak traces, re-subscribe to watermarking{' '}
                  <InlineLink to="/settings/billing">here</InlineLink>
                </>
              )
            }
          />
        </div>
      </div>

      {showSpinner && <Spinner />}

      {watermarkingLeaks.items.length === 0 && !watermarkingLeaks.searching && (
        <WatermarkingEmptyState type={TRACK} />
      )}

      {watermarkingLeaks.items.length !== 0 && (
        <InfiniteScroll
          hideLoadingLabel={showSpinner}
          next={watermarkingLeaks.nextPage}
          loadNext={url => dispatch(loadNextWatermarking(url, TRACK))}
        >
          {watermarkingLeaks.items.length !== 0 && (
            <DataTable
              appearance="bordered"
              columns={[
                {
                  name: 'Track',
                  width: 'large',
                  render: (prop: LeakTraceItem) => {
                    const track = getLastTrackData(prop)?.track || {}
                    return (
                      <MediaItem
                        media={<TrackArtwork track={track} />}
                        title={
                          track.name
                            ? getTrackStatus(track).line1
                            : prop.file_name
                        }
                        caption={
                          track.name ? (
                            <TextOverflow content={getTrackSubheader(track)} />
                          ) : null
                        }
                      />
                    )
                  },
                },
                {
                  name: 'Playlist',
                  width: 'small',
                  render: (prop: LeakTraceItem) => (
                    <TextOverflow
                      content={getLastTrackData(prop)?.playlist?.name}
                    />
                  ),
                },
                {
                  name: 'Shared with',
                  width: 'medium',
                  render: (prop: LeakTraceItem) => {
                    const recipient_data =
                      getLastTrackData(prop)?.recipient_data
                    if (!recipient_data) return null
                    const title =
                      recipient_data?.user_email ||
                      recipient_data?.business_hostname
                    return <TextOverflow content={title} />
                  },
                },
                {
                  name: 'Shared by',
                  width: 'medium',
                  render: (prop: LeakTraceItem) => {
                    const sender_data = getLastTrackData(prop)?.sender_data
                    if (!sender_data) return null
                    return <TextOverflow content={sender_data?.user_email} />
                  },
                },
                {
                  name: 'Reported',
                  width: 'small',
                  render: (prop: LeakTraceItem) => (
                    <TextOverflow
                      content={dayjs(prop.created_at).format('YYYY-MM-DD')}
                    />
                  ),
                },
                {
                  name: 'Reported by',
                  width: 'small',
                  render: (prop: LeakTraceItem) => (
                    <Tooltip content={prop.reported_by.email}>
                      <UserAvatar
                        name={prop.reported_by}
                        size="24px"
                        fontSize="11px"
                      />
                    </Tooltip>
                  ),
                },
                {
                  name: 'Status',
                  width: 'content',
                  render: (prop: LeakTraceItem) => {
                    const { label, tooltip } = getWatermarkingLeakStatus(
                      prop,
                      t,
                    )
                    return (
                      <div className="LeakTracing_Status">
                        <Tooltip content={tooltip}>
                          <InfoIcon size={24} />
                        </Tooltip>
                        {label}
                      </div>
                    )
                  },
                },
              ]}
              data={watermarkingLeaks.items}
            />
          )}
        </InfiniteScroll>
      )}
      <Outlet />
    </>
  )
}

export default LeakTracing
